import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { ShareButtonProps, ShareButton } from '..';
import { Omit } from '../../types';
import { classes } from './ShareButton.visual.st.css';
import { VisualAsync } from '../../test/visual/VisualAsync';
import { shareButtonDriverFactory } from './ShareButton.driver';

const VisualShareButton = (
  props: Omit<ShareButtonProps, 'shareData' | 'onClick'>,
) => (
  <ShareButton
    shareData={{
      title: 'Share title',
      url: 'https://wix.com',
    }}
    onClick={() => {}}
    {...props}
  />
);

const WstTest = ({
  variables,
}: {
  variables: Record<`--wst-${string}`, string>;
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      padding: '20px',
      ...variables,
    }}
  >
    <VisualShareButton withIcon />
    <VisualShareButton text="text only" />
    <VisualShareButton text="with icon" withIcon />
    <VisualShareButton text="disabled" withIcon disabled />
    <VisualShareButton text="share with long text" withIcon />
  </div>
);

visualize('ShareButton', () => {
  story('render', () => {
    snap('icon', <VisualShareButton withIcon />);
    snap('text', <VisualShareButton text="Share" />);
    snap('text+icon', <VisualShareButton text="Share" withIcon />);
  });

  story('styles overrides', () => {
    snap(
      'default',
      <VisualShareButton text="Share" withIcon className={classes.root} />,
    );
  });

  const focusTests = [
    { it: 'icon', props: { withIcon: true } },
    { it: 'text', props: { text: 'Share' } },
    { it: 'text+icon', props: { text: 'Share', withIcon: true } },
  ];

  const clickFocusTests = [
    {
      describe: 'focus',
      its: focusTests.map(({ it, props }) => ({
        it,
        props: { ...props },
      })),
    },
    {
      describe: 'focus disabled',
      its: focusTests.map(({ it, props }) => ({
        it,
        props: { ...props, disabled: true },
      })),
    },
  ];

  clickFocusTests.forEach(({ describe, its }) =>
    story(describe, () => {
      its.forEach(({ it, props }) =>
        snap(it, (done) => (
          <VisualAsync
            driverFactory={shareButtonDriverFactory}
            onDone={done}
            action={(driver) => driver.click()}
          >
            <VisualShareButton {...props} />
          </VisualAsync>
        )),
      );
    }),
  );

  story('wst variables', () => {
    snap(
      'primary background',
      <WstTest
        variables={{
          '--wst-primary-background-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'paragraph 2',
      <WstTest
        variables={{
          '--wst-paragraph-2-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'system disabled',
      <WstTest
        variables={{
          '--wst-system-disabled-color-rgb': '0,255,0',
        }}
      />,
    );
  });
});
